import { ReactElement } from "react";
import styled from "styled-components";
import { track } from "lib/analytics/analytics";
import { AnalyticsEvents } from "lib/analytics/constants";
import responsiveStyling from "lib/responsive";
import {
  BrandColorPalette,
  PrimaryColorPalette,
  StatusColorPalette,
  TextAndShapesPalette,
} from "lib/styles/colors";

/**
 * Store URLs and badge artwork from the 10ure.com mariners page
 * (https://www.10ure.com/mariners). Do not substitute other store links.
 */
export const MARINER_APP_STORE_URL =
  "https://apps.apple.com/us/app/id1530699354";
export const MARINER_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.tenure.tenuremobile";

interface SplitStyleProps {
  $split?: boolean;
}

const splitSectionOverride = `
  margin-bottom: 0;
  ${responsiveStyling({
    ">1024": `
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
    `,
  })}
`;

const splitCopyOverride = responsiveStyling({
  ">1024": `
    flex: 0 1 auto;
    margin-bottom: 16px;
    margin-right: 0;
  `,
});

const splitBadgeRowOverride = `
  margin-top: auto;
  ${responsiveStyling({
    ">1024": `
      width: 100%;
      max-width: 100%;
      flex-shrink: 1;
      justify-content: flex-start;
    `,
  })}
`;

const Section = styled.section<SplitStyleProps>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin-bottom: 24px;
  padding: 20px;
  background-color: ${PrimaryColorPalette.MAIN_6};
  border: 1px solid ${PrimaryColorPalette.MAIN_4};
  border-radius: 12px;
  ${responsiveStyling({
    ">1024": `
      flex-direction: row;
      align-items: center;
      padding: 24px 32px;
    `,
  })};
  ${({ $split }) => ($split ? splitSectionOverride : "")}
`;

const Copy = styled.div<SplitStyleProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin-bottom: 16px;
  ${responsiveStyling({
    ">1024": `
      flex: 1 1 auto;
      margin-bottom: 0;
      margin-right: 32px;
    `,
  })};
  ${({ $split }) => ($split ? splitCopyOverride : "")}
`;

const Headline = styled.h2`
  margin: 0;
  color: ${TextAndShapesPalette.BRAND_BLACK};
  font-family: Gilroy;
  font-weight: 900;
  font-size: 22px;
  line-height: 28px;
  letter-spacing: 0.2px;
  text-align: left;
  overflow-wrap: break-word;
  ${responsiveStyling({
    ">1024": `
      font-size: 28px;
      line-height: 34px;
    `,
  })};
`;

const Subtext = styled.p`
  margin: 8px 0 0;
  color: ${TextAndShapesPalette.INPUT};
  font-family: SFProDisplay;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: left;
  overflow-wrap: break-word;
`;

const Note = styled.p`
  margin: 8px 0 0;
  color: ${BrandColorPalette.ACTION};
  font-family: SFProDisplay;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
`;

const BadgeRow = styled.div<SplitStyleProps>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  ${responsiveStyling({
    ">1024": `
      width: auto;
      flex-shrink: 0;
      justify-content: flex-end;
    `,
  })};
  ${({ $split }) => ($split ? splitBadgeRowOverride : "")}
`;

const StoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  line-height: 0;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    opacity: 0.92;
  }

  &:focus {
    outline: 3px solid ${StatusColorPalette.WARNING_DECORATION};
    outline-offset: 3px;
  }
`;

const BadgeImage = styled.img`
  display: block;
  height: 44px;
  width: auto;
  max-width: 100%;
  ${responsiveStyling({
    ">480": `
      height: 48px;
    `,
  })};
`;

interface MobileAppDownloadCtasProps {
  /**
   * Jobs page half-width card. Badges stay under the copy.
   * News leaves this unset and keeps the wide banner.
   */
  split?: boolean;
}

export default function MobileAppDownloadCtas({
  split = false,
}: MobileAppDownloadCtasProps): ReactElement {
  const handleAppStore = () => {
    track(AnalyticsEvents.DL_APP_STORE_CLICKED);
  };

  const handleGooglePlay = () => {
    track(AnalyticsEvents.DL_GOOGLE_PLAY_CLICKED);
  };

  return (
    <Section $split={split} aria-label="Download the 10ure mobile app">
      <Copy $split={split}>
        <Headline>Your whole career at sea, in one free app.</Headline>
        <Subtext>
          Carry your credentials, log your sea time and apply to jobs from your
          phone.
        </Subtext>
        <Note>Free. No ads. No spam.</Note>
      </Copy>
      <BadgeRow $split={split}>
        <StoreLink
          href={MARINER_APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download on the App Store (opens in a new tab)"
          onClick={handleAppStore}
        >
          <BadgeImage
            src="/images/apple-app-store-badge.png"
            alt="Download on the App Store badge with Apple logo"
            width={380}
            height={113}
          />
        </StoreLink>
        <StoreLink
          href={MARINER_PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Get it on Google Play (opens in a new tab)"
          onClick={handleGooglePlay}
        >
          <BadgeImage
            src="/images/google-play-badge.png"
            alt="Get it on Google Play badge"
            width={380}
            height={113}
          />
        </StoreLink>
      </BadgeRow>
    </Section>
  );
}
